import { applyTheme, resolveTheme, type ThemePreference } from '@hoedokloop/design';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const storageKey = 'hoedokloop.theme';

function storedPreference(): ThemePreference {
  const value = localStorage.getItem(storageKey);
  return value === 'light' || value === 'dark' || value === 'system' ? value : 'system';
}

type ThemeContextValue = {
  preference: ThemePreference;
  setPreference: (next: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({
  children,
  darkAllowed = true,
}: PropsWithChildren<{ darkAllowed?: boolean }>) {
  const [preference, setPreferenceState] = useState<ThemePreference>(storedPreference);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => {
      applyTheme(
        document.documentElement,
        resolveTheme({ preference, systemDark: media.matches, darkAllowed }),
      );
    };
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [darkAllowed, preference]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      setPreference: (next) => {
        const allowed = darkAllowed || next !== 'dark' ? next : 'light';
        localStorage.setItem(storageKey, allowed);
        setPreferenceState(allowed);
      },
    }),
    [darkAllowed, preference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used within ThemeProvider');
  return value;
}
